import { MdAdd, MdRemove, MdOutlineDescription, MdChevronRight, MdPropaneTank, MdKeyboardArrowUp, MdUnfoldLess } from "react-icons/md";
import { GasTypeOption, GasTypeValue, Order, WeightSize, WeightOption } from "../utils/type";
import { useState, useEffect, useMemo } from "react";
import { sizeMap, getGasImageByKg } from "../utils/styleHelper";
import { GasPriceItem } from "../utils/mockApi";
import { HIDDEN_GAS_TYPES } from "@utils/func/gasTitle";
import "./ProductCard.scss";

const SIZE_BY_KG: Record<number, WeightSize> = {
  5: "small",
  10: "medium",
  16: "large",
  20: "large",
  50: "xlarge",
};
const COLOR_BY_KG: Record<number, string> = {
  5: "text-blue-400",
  10: "text-blue-500",
  16: "text-blue-600",
  20: "text-blue-700",
  50: "text-blue-800",
};
const POPULAR_KG = new Set([16, 20]);

/** API 可能回傳的瓦斯類型（後端 gasType 欄位值） */
export const GAS_TYPE_API = {
  瓦斯: "瓦斯",
  液化石油氣: "液化石油氣",
  液化氣: "液化氣",
  混合氣: "混合氣",
  丙烷: "丙烷",
  純丙烷: "純丙烷",
} as const;
export type GasTypeApiValue = (typeof GAS_TYPE_API)[keyof typeof GAS_TYPE_API];

/** 液化氣相關叫法（皆視為同一類、isPopular） */
const LPG_ALIASES = new Set<string>(["液化氣", "液化石油氣"]);

/** 每種 API 類型的顯示名稱、前端 value、是否熱門 */
const GAS_TYPE_CONFIG: Record<string, { label: string; value: GasTypeValue; isPopular: boolean }> = {
  [GAS_TYPE_API.瓦斯]: { label: "瓦斯", value: "gas", isPopular: false },
  [GAS_TYPE_API.液化石油氣]: { label: "液化氣", value: "gas", isPopular: true },
  [GAS_TYPE_API.液化氣]: { label: "液化氣", value: "gas", isPopular: true },
  [GAS_TYPE_API.混合氣]: { label: "混合氣", value: "gas", isPopular: false },
  [GAS_TYPE_API.丙烷]: { label: "純丙烷", value: "propane", isPopular: false },
  [GAS_TYPE_API.純丙烷]: { label: "純丙烷", value: "propane", isPopular: false },
};

/** 依 GasTypeValue 反查可匹配的 API gasType 字串（供 findMatchingPrice 用） */
const API_GAS_TYPES_BY_VALUE: Record<GasTypeValue, string[]> = {
  gas: ["瓦斯", "液化石油氣", "液化氣", "混合氣"],
  propane: ["丙烷", "純丙烷"],
};

function gasTypeApiToValue(apiType: string): GasTypeValue {
  const cfg = GAS_TYPE_CONFIG[apiType?.trim() ?? ""];
  return cfg?.value ?? (apiType?.includes("丙烷") ? "propane" : "gas");
}
function gasTypeApiToLabel(apiType: string): string {
  const cfg = GAS_TYPE_CONFIG[apiType?.trim() ?? ""];
  return cfg?.label ?? (apiType?.includes("丙烷") ? "純丙烷" : apiType || "瓦斯");
}
function gasTypeApiIsPopular(apiType: string): boolean {
  if (!apiType) return false;
  const t = apiType.trim();
  return GAS_TYPE_CONFIG[t]?.isPopular ?? LPG_ALIASES.has(t);
}

/** 從 API 商品價格列表推導可選的重量選項 */
function deriveWeightOptions(list: GasPriceItem[]): WeightOption[] {
  const kgSet = new Set(list.map((p) => p.gasCylinderInfo.kilogram));
  return Array.from(kgSet)
    .sort((a, b) => a - b)
    .map((kg) => ({
      value: String(kg),
      label: `${kg}KG`,
      isPopular: POPULAR_KG.has(kg),
      size: SIZE_BY_KG[kg] ?? "large",
      color: COLOR_BY_KG[kg] ?? "text-blue-600",
    }));
}

/** 從 API 商品價格列表推導可選的瓦斯類型（同 label 只保留一筆，液化氣／液化石油氣 合併為「液化氣」） */
function deriveGasTypeOptions(list: GasPriceItem[]): GasTypeOption[] {
  const seenByLabel = new Set<string>();
  const options: GasTypeOption[] = [];
  for (const p of list) {
    const t = p.gasCylinderInfo.gasType?.trim();
    if (!t) continue;
    const label = gasTypeApiToLabel(t);
    if (seenByLabel.has(label)) continue;
    seenByLabel.add(label);
    options.push({
      value: gasTypeApiToValue(t),
      label,
      isPopular: gasTypeApiIsPopular(t),
    });
  }
  return options;
}

const ProductCard = ({
  order,
  setOrder,
  gasPriceList = [],
}: {
  order: Order;
  setOrder: React.Dispatch<React.SetStateAction<Order>>;
  gasPriceList?: GasPriceItem[];
}) => {
  const [expandedItemId, setExpandedItemId] = useState<number | null>(1);
  const [collapsingItemId, setCollapsingItemId] = useState<number | null>(null);
  /** 目前顯示的圖片 src（等新圖載完再切換，避免閃爍） */
  const [displayedSrcByItemId, setDisplayedSrcByItemId] = useState<Record<number, string>>({});
  /** 正在播放切換動畫的品項 id */
  const [animatingItemId, setAnimatingItemId] = useState<number | null>(null);

  // 從 API 商品價格列表推導可選的重量與類型（與價格同一來源）
  const weightOptions = useMemo(() => deriveWeightOptions(gasPriceList), [gasPriceList]);
  const gasTypeOptions = useMemo(() => deriveGasTypeOptions(gasPriceList), [gasPriceList]);

  // 當 gasPriceList 載入後，更新現有項目的價格信息；若僅一筆且無 priceId 則以 API 第一筆為預設
  useEffect(() => {
    if (gasPriceList.length === 0) return;

    setOrder((prev) => {
      const mapped = prev.items.map((item) => {
        if (item.priceId) return item;
        const matchedPrice = findMatchingPrice(item.weight, item.type);
        if (matchedPrice) {
          return {
            ...item,
            priceId: matchedPrice.gpId ?? matchedPrice.cisGpId ?? undefined,
            unitPrice: matchedPrice.price,
            gasKilogram: matchedPrice.gasCylinderInfo.kilogram,
            gasType: matchedPrice.gasCylinderInfo.gasType,
            isCisGas: matchedPrice.cisGpId != null,
          };
        }
        return item;
      });

      // 新增模式：若只有一筆且仍無 priceId，改為使用 API 第一筆商品
      const singleNoPrice = mapped.length === 1 && !mapped[0].priceId;
      if (singleNoPrice && gasPriceList[0]) {
        const p = gasPriceList[0];
        return {
          ...prev,
          items: [
            {
              id: 1,
              weight: String(p.gasCylinderInfo.kilogram),
              type: gasTypeApiToValue(p.gasCylinderInfo.gasType),
              quantity: 1,
              priceId: p.gpId ?? p.cisGpId ?? undefined,
              unitPrice: p.price,
              gasKilogram: p.gasCylinderInfo.kilogram,
              gasType: p.gasCylinderInfo.gasType,
              isCisGas: p.cisGpId != null,
            },
          ],
        };
      }
      return { ...prev, items: mapped };
    });
  }, [gasPriceList.length]);

  const updateQuantity = (id: number, delta: number) => {
    setOrder((prev) => ({
      ...prev,
      items: prev.items.map((item) => (item.id === id ? { ...item, quantity: Math.max(1, item.quantity + delta) } : item)),
    }));
  };

  const addItem = () => {
    const newId = Math.max(...order.items.map((i) => i.id), 0) + 1;
    // 預設使用 API 商品列表第一筆，若無則 20kg 液化氣
    const firstPrice = gasPriceList[0];
    const defaultWeight = firstPrice ? String(firstPrice.gasCylinderInfo.kilogram) : "20";
    const defaultType: GasTypeValue = firstPrice ? gasTypeApiToValue(firstPrice.gasCylinderInfo.gasType) : "gas";
    const matchedPrice = findMatchingPrice(defaultWeight, defaultType) ?? firstPrice ?? null;

    if (!matchedPrice) return;

    setOrder((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          id: newId,
          weight: defaultWeight,
          type: defaultType,
          quantity: 1,
          priceId: matchedPrice.gpId ?? matchedPrice.cisGpId ?? undefined,
          unitPrice: matchedPrice.price,
          gasKilogram: matchedPrice.gasCylinderInfo.kilogram,
          gasType: matchedPrice.gasCylinderInfo.gasType,
          isCisGas: matchedPrice.cisGpId != null,
        },
      ],
    }));
    setExpandedItemId(newId); // 自動展開新品項
  };

  const removeItem = (id: number) => {
    if (order.items.length > 1) {
      const remainingItems = order.items.filter((item) => item.id !== id);
      setOrder((prev) => ({
        ...prev,
        items: remainingItems,
      }));
      // 如果刪除的是展開的品項，展開第一個品項
      if (expandedItemId === id) {
        if (remainingItems.length > 0) {
          setExpandedItemId(remainingItems[0].id);
        } else {
          setExpandedItemId(null);
        }
      }
    }
  };

  const collapseItem = (itemId: number) => {
    setCollapsingItemId(itemId);
    setTimeout(() => {
      setExpandedItemId(null);
      setCollapsingItemId(null);
    }, 300); // 等待動畫完成
  };

  const collapseAll = () => {
    if (expandedItemId !== null) {
      setCollapsingItemId(expandedItemId);
      setTimeout(() => {
        setExpandedItemId(null);
        setCollapsingItemId(null);
      }, 300);
    }
  };

  // 根據 weight 和 type 匹配商品價格（type 對應的 API gasType 可能多種，皆可匹配）
  const findMatchingPrice = (weight: string, type: GasTypeValue): GasPriceItem | null => {
    const weightNum = parseInt(weight, 10);
    const allowedApiTypes = API_GAS_TYPES_BY_VALUE[type] ?? [];

    const match = gasPriceList.find(
      (price) => price.gasCylinderInfo.kilogram === weightNum && allowedApiTypes.includes(price.gasCylinderInfo.gasType?.trim() ?? ""),
    );

    return match || null;
  };

  const updateItemWeight = (id: number, newWeight: string) => {
    setOrder((prev) => ({
      ...prev,
      items: prev.items.map((item) => {
        if (item.id === id) {
          const matchedPrice = findMatchingPrice(newWeight, item.type);
          return {
            ...item,
            weight: newWeight,
            priceId: matchedPrice?.gpId || matchedPrice?.cisGpId || undefined,
            unitPrice: matchedPrice?.price,
            gasKilogram: matchedPrice?.gasCylinderInfo.kilogram,
            gasType: matchedPrice?.gasCylinderInfo.gasType,
            isCisGas: matchedPrice?.cisGpId !== null && matchedPrice?.cisGpId !== undefined,
          };
        }
        return item;
      }),
    }));
  };

  const updateItemType = (id: number, newType: GasTypeValue) => {
    setOrder((prev) => ({
      ...prev,
      items: prev.items.map((item) => {
        if (item.id === id) {
          const matchedPrice = findMatchingPrice(item.weight, newType);
          return {
            ...item,
            type: newType,
            priceId: matchedPrice?.gpId || matchedPrice?.cisGpId || undefined,
            unitPrice: matchedPrice?.price,
            gasKilogram: matchedPrice?.gasCylinderInfo.kilogram,
            gasType: matchedPrice?.gasCylinderInfo.gasType,
            isCisGas: matchedPrice?.cisGpId !== null && matchedPrice?.cisGpId !== undefined,
          };
        }
        return item;
      }),
    }));
  };

  return (
    <div className="bg-white rounded-2xl p-6 shadow-lg">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center space-x-3">
          <div className="p-2.5 bg-blue-100 rounded-xl">
            <MdOutlineDescription className="text-blue-600 w-4 h-4" />
          </div>
          <h2 className="text-md font-bold text-gray-800">瓦斯品項 / 數量</h2>
        </div>
        {/* 全部收合按鈕 */}
        {order.items.length > 1 && (
          <button
            onClick={collapseAll}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all">
            <MdUnfoldLess className="w-4 h-4" />
            <span>全部收合</span>
          </button>
        )}
      </div>

      {order.items.map((item, idx) => {
        const isExpanded = expandedItemId === item.id;
        const isCollapsing = collapsingItemId === item.id;
        const selectedWeight = weightOptions.find((w) => w.value === item.weight);
        const selectedType = gasTypeOptions.find((t) => t.value === item.type);
        const sizeKey: WeightSize = selectedWeight?.size ?? "large";

        return (
          <div
            key={item.id}
            className={`mb-3 last:mb-0 overflow-hidden transition-all duration-400 ${
              isExpanded && !isCollapsing ? "card-expanded" : ""
            } ${isCollapsing ? "card-collapsed" : ""}`}>
            {isExpanded || isCollapsing ? (
              // 展開狀態 - 完整編輯介面
              <div className="bg-white rounded-2xl shadow-lg border-2 border-blue-200 transition-all overflow-hidden">
                {/* 上半部：滿版瓦斯桶圖片區域（無留白） */}
                <div className="relative h-[200px] border-b-2 border-gray-100 overflow-hidden">
                  {/* 頂部控制列 */}
                  <div className="absolute top-2 left-2 right-2 flex items-center justify-between z-10">
                    {/* 左側：品項標籤 */}
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-white/90 backdrop-blur-sm rounded-lg shadow-sm border border-gray-200">
                      <div className="w-1.5 h-4 bg-blue-500 rounded-full"></div>
                      <span className="text-xs font-bold text-gray-700">品項 {idx + 1}</span>
                    </div>

                    {/* 右側：移除與收合按鈕 */}
                    <div className="flex items-center gap-2">
                      {/* 移除按鈕 */}
                      {order.items.length > 1 && (
                        <button
                          onClick={() => removeItem(item.id)}
                          className="px-3 py-1.5 bg-red-500/90 backdrop-blur-sm hover:bg-red-600 text-white rounded-lg text-xs font-medium transition-all shadow-sm active:scale-95">
                          移除
                        </button>
                      )}
                      {/* 收合按鈕 */}
                      <button
                        onClick={() => collapseItem(item.id)}
                        className="p-2 bg-white/90 backdrop-blur-sm hover:bg-white text-gray-600 hover:text-blue-600 rounded-lg transition-all shadow-sm border border-gray-200"
                        title="收合">
                        <MdKeyboardArrowUp className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* 瓦斯桶圖片：預載後再切換＋微縮放動畫，避免閃爍 */}
                  <img
                    src={displayedSrcByItemId[item.id] ?? getGasImageByKg(item.weight)}
                    alt={selectedWeight?.label ?? "瓦斯"}
                    className={`product-card-gas-img absolute inset-0 w-full h-full object-cover object-center ${animatingItemId === item.id ? "product-card-gas-img-anim" : ""}`}
                  />
                  {/* 隱藏預載用，載完才更新顯示的圖 */}
                  <img
                    src={getGasImageByKg(item.weight)}
                    alt=""
                    aria-hidden
                    className="sr-only"
                    onLoad={() => {
                      const src = getGasImageByKg(item.weight);
                      setDisplayedSrcByItemId((prev) => (prev[item.id] === src ? prev : { ...prev, [item.id]: src }));
                      setAnimatingItemId(item.id);
                      setTimeout(() => setAnimatingItemId(null), 220);
                    }}
                  />

                  {/* 底部：重量標籤 */}
                  <div className="absolute bottom-2 left-2 z-10">
                    <div className="px-4 py-2 bg-white/90 backdrop-blur-sm rounded-lg shadow-sm border border-gray-200">
                      <span className="text-sm font-bold text-gray-700">{selectedWeight?.label}</span>
                    </div>
                  </div>
                </div>

                {/* 下半部：編輯控制區域 */}
                <div className="p-5">
                  {/* 重量選擇 */}
                  <div className="mb-4">
                    <label className="block text-xs font-semibold text-gray-500 mb-2">選擇重量</label>
                    <div className="relative">
                      <select
                        value={item.weight}
                        onChange={(e) => updateItemWeight(item.id, e.target.value)}
                        className="w-full px-4 py-4 border-2 border-gray-200 rounded-xl font-medium text-center focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all appearance-none bg-white"
                        style={{
                          backgroundImage:
                            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23666' d='M10.293 3.293L6 7.586 1.707 3.293A1 1 0 00.293 4.707l5 5a1 1 0 001.414 0l5-5a1 1 0 10-1.414-1.414z'/%3E%3C/svg%3E\")",
                          backgroundRepeat: "no-repeat",
                          backgroundPosition: "right 1rem center",
                        }}>
                        {weightOptions.map((weight) => (
                          <option key={weight.value} value={weight.value}>
                            {weight.label} {weight.isPopular ? "(常用)" : ""}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* 類型選擇（丙烷、液化氣 等不顯示氣別字樣；邏輯保留，僅 UI 不提供選擇；無可選項目時整段隱藏） */}
                  {gasTypeOptions.filter((type) => type.value !== "propane" && !HIDDEN_GAS_TYPES.has(type.label)).length > 0 && (
                  <div className="mb-4">
                    <label className="block text-xs font-semibold text-gray-500 mb-2">選擇類型</label>
                    <div className="grid grid-cols-1 gap-2">
                      {gasTypeOptions
                        .filter((type) => type.value !== "propane" && !HIDDEN_GAS_TYPES.has(type.label))
                        .map((type: GasTypeOption) => (
                          <button
                            key={type.value}
                            type="button"
                            onClick={() => updateItemType(item.id, type.value)}
                            className={`
                                     p-3 rounded-xl transition-all border-2 font-medium text-sm
                                     ${
                                       item.type === type.value
                                         ? "bg-blue-500 border-blue-500 text-white shadow-lg"
                                         : "bg-white border-gray-200 text-gray-700 hover:border-blue-300 hover:bg-blue-50"
                                     }
                                   `}>
                            <div className="flex flex-col items-center space-y-0.5">
                              <span>{type.label}</span>
                              {type.isPopular && (
                                <span className={`text-xs ${item.type === type.value ? "text-blue-100" : "text-gray-400"}`}>常用</span>
                              )}
                            </div>
                          </button>
                        ))}
                    </div>
                  </div>
                  )}

                  <div className="space-y-3">
                    {/* 價格顯示 */}
                    {/* {item.unitPrice && (
                      <div className="bg-gradient-to-r from-blue-50 to-yellow-50 rounded-xl p-3 border border-blue-200">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">單價</span>
                          <span className="text-lg font-bold text-blue-600">
                            ${item.unitPrice.toLocaleString()}
                          </span>
                        </div>
                        <div className="flex items-center justify-between mt-1">
                          <span className="text-xs text-gray-500">小計</span>
                          <span className="text-base font-semibold text-gray-700">
                            ${(item.unitPrice * item.quantity).toLocaleString()}
                          </span>
                        </div>
                      </div>
                    )} */}

                    {/* 數量選擇 */}
                    <div className="flex items-center justify-between bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl p-3 border border-gray-200">
                      <span className="text-gray-700 font-semibold">數量</span>
                      <div className="flex items-center space-x-4">
                        <button
                          onClick={() => updateQuantity(item.id, -1)}
                          className="w-9 h-9 bg-white border-2 border-gray-300 rounded-xl flex items-center justify-center hover:border-blue-500 hover:bg-blue-50 transition-all active:scale-95">
                          <MdRemove size={18} className="text-gray-600" />
                        </button>
                        <span className="text-xl font-bold text-gray-800 w-10 text-center">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.id, 1)}
                          className="w-9 h-9 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center hover:from-blue-600 hover:to-blue-700 transition-all active:scale-95 shadow-md">
                          <MdAdd size={18} className="text-white" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              // 收合狀態 - 簡易顯示
              <button
                onClick={() => setExpandedItemId(item.id)}
                className="w-full p-4 bg-white hover:bg-blue-50 border-2 border-gray-200 hover:border-blue-300 rounded-xl transition-all text-left shadow-sm hover:shadow-md">
                <div className="flex items-center gap-4">
                  {/* 小型瓦斯桶圖示 */}
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg border border-gray-200 flex items-center justify-center transition-all">
                    <MdPropaneTank className={`w-5 h-7 ${selectedWeight?.color || "text-blue-600"} transition-colors`} />
                  </div>

                  {/* 品項資訊 */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-semibold text-gray-500">品項 {idx + 1}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm font-medium text-gray-800">
                      <span>{selectedWeight?.label}</span>
                      <span className="text-gray-400">·</span>
                      <span>{item.type === "propane" ? "瓦斯" : selectedType?.label}</span>
                      <span className="text-gray-400">·</span>
                      <span className="text-blue-600">x{item.quantity}</span>
                    </div>
                  </div>

                  {/* 展開圖示 */}
                  <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                    <MdChevronRight className="w-4 h-4 text-gray-600" />
                  </div>
                </div>
              </button>
            )}
          </div>
        );
      })}

      <button
        onClick={addItem}
        disabled={gasPriceList.length === 0}
        className="w-full mt-4 py-3.5 border-2 border-dashed border-blue-300 text-blue-600 rounded-xl flex items-center justify-center space-x-2 font-semibold hover:bg-blue-50 hover:border-blue-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent">
        <MdAdd size={20} />
        <span>新增品項</span>
      </button>
    </div>
  );
};

export default ProductCard;
